/* ======================================================================
   RAW COMICS — SITE BEHAVIOR
   Mobile nav toggle, active-link highlighting, scroll reveals,
   cart-count stub. No dependencies.

   LIQUID CONVERSION: ships almost unchanged as an asset. The cart-count
   stub is replaced by Liquid ({{ cart.item_count }} rendered
   server-side in the header snippet) — delete updateCartCount() then.
   ====================================================================== */

(function () {
  'use strict';

  /* ── Mobile nav toggle ─────────────────────────────────────────── */
  var header = document.getElementById('site-header');
  var toggle = document.querySelector('[data-nav-toggle]');

  /* ── Landing fold ──────────────────────────────────────────────
     The landing block (banner + library buttons) is sized to the window less
     the header. The header's height is measured, never assumed: the nav wraps
     to a second line at some widths and the fold would miss by a row. An OPEN
     mobile menu is never measured -- it makes the header four times taller,
     and a resize while it is open (URL bar, keyboard, rotate) would freeze
     that height into --hdr for the life of the page (audit 2026-09-06). The
     first measurement is the inline script right after the header markup, so
     the first paint is already right; this one keeps it right. */
  var setHdr = function () {
    if (header && !header.classList.contains('nav-open')) {
      document.documentElement.style.setProperty('--hdr', header.getBoundingClientRect().height + 'px');
    }
  };
  setHdr();
  window.addEventListener('resize', setHdr);
  if (document.fonts && document.fonts.ready) { document.fonts.ready.then(setHdr); }

  if (header && toggle) {
    toggle.addEventListener('click', function () {
      var open = header.classList.toggle('nav-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) { setHdr(); }
    });
  }

  /* ── Library carousel ──────────────────────────────────────────
     Two pages of six wall-btn tiles (page 1: shelves, page 2: series);
     either arrow flips to the other page since there are only two. The
     markup ships both arrows and page 2 with the `hidden` attribute
     already set -- this is the ONLY thing that reveals the arrows. There
     is no fallback path that shows page 2 without this running, by
     design: with JS off, page 1 (never hidden) is the whole page. */
  var carouselTrack = document.querySelector('[data-carousel-track]');
  var carouselPages = document.querySelectorAll('[data-carousel-page]');
  var carouselArrows = document.querySelectorAll('[data-carousel-arrow]');
  if (carouselTrack && carouselPages.length === 2 && carouselArrows.length) {
    var carouselAt = 0;
    var CAROUSEL_LABELS = ['Show series shelves', 'Show library shelves'];
    var renderCarousel = function () {
      carouselPages[0].hidden = carouselAt !== 0;
      carouselPages[1].hidden = carouselAt !== 1;
      var label = CAROUSEL_LABELS[carouselAt];
      Array.prototype.forEach.call(carouselArrows, function (btn) {
        btn.setAttribute('aria-label', label);
      });
    };
    Array.prototype.forEach.call(carouselArrows, function (btn) {
      btn.addEventListener('click', function () {
        carouselAt = carouselAt === 0 ? 1 : 0;
        renderCarousel();
      });
      btn.hidden = false;
    });
    renderCarousel();
  }

  /* ── Cover skeletons ───────────────────────────────────────────
     Retires the shimmer on each cover box once its image is in. Three ways a
     box settles, and every one of them ends the animation:
       - the image is already decoded when this runs (cached, or eager),
       - it fires load or error later (lazy covers below the fold),
       - the box holds no <img> at all (.cover-placeholder), so nothing will
         ever fire and it settles immediately.
     Failing to settle costs an unseen animation, never a hidden cover. */
  var covers = document.querySelectorAll('.product-card__media,.product-detail__cover');
  Array.prototype.forEach.call(covers, function (box) {
    var settle = function () { box.classList.add('is-loaded'); };
    var img = box.querySelector('img');
    if (!img) { settle(); return; }
    if (img.complete) { settle(); return; }
    img.addEventListener('load', settle);
    img.addEventListener('error', settle);
  });

  /* ── Active nav link (body[data-page] ↔ a[data-nav]) ───────────── */
  var page = document.body.getAttribute('data-page');
  if (page) {
    var active = document.querySelector('.main-nav__link[data-nav="' + page + '"]');
    if (active) {
      active.classList.add('is-active');
      active.setAttribute('aria-current', 'page');
    }
  }

  /* ── Active category highlight (strip chips + home tiles) ──────────
     When the current URL carries a facet filter (?era=/publisher=/
     special=/genre=), mark the matching .category-chip / .category-tile
     as selected — so clicking a category shows it highlighted, the same
     way the filter panel does. Multi-value (comma) params supported. */
  (function () {
    var params = new URLSearchParams(window.location.search);
    var chips = document.querySelectorAll('.category-chip, .category-tile');
    Array.prototype.forEach.call(chips, function (a) {
      var url = new URL(a.getAttribute('href'), window.location.href);
      var matched = false;
      ['era', 'publisher', 'special', 'genre'].forEach(function (key) {
        var linkVal = url.searchParams.get(key);
        if (!linkVal) { return; }
        var current = (params.get(key) || '').split(',').map(function (v) { return v.trim(); });
        if (current.indexOf(linkVal) !== -1) { matched = true; }
      });
      if (matched) {
        a.classList.add('is-active');
        a.setAttribute('aria-current', 'true');
      }
    });
  })();

  /* ── Scroll reveals (.reveal → .is-visible) ────────────────────── */
  var revealables = document.querySelectorAll('.reveal');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (revealables.length && 'IntersectionObserver' in window && !reduceMotion) {
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

    revealables.forEach(function (el) { observer.observe(el); });
  } else {
    /* No IO support or reduced motion: show everything immediately */
    revealables.forEach(function (el) { el.classList.add('is-visible'); });
  }

  /* ── Search: every word becomes a search term ──────────────────────
     Storefront search matches whole indexed words. The forms already send
     options[prefix]=last, which is meant to make the last term a partial
     match, but in practice "batman" returned nothing while "batman*"
     returned Batman #30 — so the indexed token is not reachable by exact
     match. Appending the wildcards ourselves gives the behaviour shoppers
     expect without them having to know the syntax.
     The rule itself lives in expandQuery() below, so the form submit and the
     dropdown cannot drift apart — they were separate copies of it before.
     Left alone if they already typed * or used "quoted phrases", so power
     users keep control. Applies to all three search forms via delegation.

     The wildcard goes out on a hidden field and the visible box is never
     rewritten, so the shopper never sees a "*" appear as they submit. The
     visible input keeps name="q" in the markup so search still works with
     JavaScript off; we only strip that name at submit time. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[role="search"]')) { return; }
    var input = form.querySelector('[data-search-input]');
    if (!input) { return; }

    // Reset to the plain state FIRST. A previous submit may have stripped the
    // visible input's name and left a hidden field holding that older query; if
    // this submit then early-returns (quoted phrase, explicit *), the form would
    // otherwise post the PREVIOUS search instead of what was just typed. Seen
    // via bfcache: search "batman", go Back, search "amazing fantasy" -> batman.
    var hidden = form.querySelector('input[type="hidden"][data-search-q]');
    input.setAttribute('name', 'q');
    if (hidden) { hidden.removeAttribute('name'); hidden.value = ''; }

    var value = input.value.trim();
    var expanded = expandQuery(value);
    // Nothing to rewrite: an empty box, a quoted phrase, or a query whose every
    // term the shopper already starred. Posting q unchanged is correct there.
    if (!value || expanded === value) { return; }
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.setAttribute('data-search-q', '');
      form.appendChild(hidden);
    }
    hidden.name = 'q';
    hidden.value = expanded;
    input.removeAttribute('name');
  });

  /* ── Just In entry points ──────────────────────────────────────────
     Every "Just In" link that can show a selected state (today: the category
     tile on the homepage) goes to /collections/all?newfirst=1 — the store page
     with the sort already toggled on. The landing-page Just In button goes
     there too but carries no data-newfirst-link: the homepage never has
     ?newfirst=1 in its own URL, so a highlight could never fire. Liquid cannot read query strings, so the selected state
     has to be applied here rather than server-side like the other chips. */
  if (window.location.search.indexOf('newfirst=1') !== -1) {
    document.querySelectorAll('[data-newfirst-link]').forEach(function (el) {
      el.classList.add('is-active');
      el.setAttribute('aria-current', 'true');
    });
  }

  /* ── Product page: point "Back" at where they actually came from ───
     The Liquid fallback is /collections/all. If the previous page was a browse
     or search view on this same store, use that instead so filters survive the
     round trip. Same-origin only, and only for listing paths — we never want
     Back to lead to checkout, an account page, or another product. */
  var backLink = document.querySelector('[data-product-back]');
  if (backLink && document.referrer) {
    try {
      var ref = new URL(document.referrer);
      var listing = /^\/(collections|search)(\/|$)/.test(ref.pathname);
      if (ref.origin === window.location.origin && listing) {
        backLink.href = ref.pathname + ref.search;
      }
    } catch (err) { /* malformed referrer — keep the Liquid fallback */ }
  }

  /* ── Product page: gallery thumbs swap the cover in place ──────────
     Without JS each thumb is a plain link to the full-size photo, so
     nothing here is load-bearing — this only saves the page navigation. */
  var galleryMain = document.querySelector('[data-gallery-main]');
  var galleryThumbs = document.querySelectorAll('[data-gallery-thumb]');
  if (galleryMain && galleryThumbs.length) {
    var galleryPrev = document.querySelector('[data-gallery-prev]');
    var galleryNext = document.querySelector('[data-gallery-next]');
    var galleryCount = document.querySelector('[data-gallery-count]');
    var galleryAt = 0;

    /* The arrows are display:none until this class lands, so they can never be
       painted as controls that do nothing. */
    var galleryCover = document.querySelector('.product-detail__cover');
    if (galleryCover && (galleryPrev || galleryNext)) {
      galleryCover.classList.add('gallery-ready');
    }

    /* The thumb strip is the single source of truth for the photo list: the
       arrows move a cursor through THAT list, so the strip and the cover can
       never disagree about which photo is showing. */
    var showPhoto = function (i) {
      var n = galleryThumbs.length;
      galleryAt = ((i % n) + n) % n;            /* wraps both directions */
      var thumb = galleryThumbs[galleryAt];
      galleryMain.src = thumb.getAttribute('data-src');
      galleryMain.srcset = thumb.getAttribute('data-srcset');
      galleryMain.alt = thumb.getAttribute('data-alt') || '';
      Array.prototype.forEach.call(galleryThumbs, function (t) {
        t.classList.remove('is-active');
      });
      thumb.classList.add('is-active');
      if (galleryCount) {
        galleryCount.textContent = (galleryAt + 1) + ' / ' + n;
      }
    };

    Array.prototype.forEach.call(galleryThumbs, function (thumb, i) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        showPhoto(i);
      });
    });

    if (galleryPrev) {
      galleryPrev.addEventListener('click', function () { showPhoto(galleryAt - 1); });
    }
    if (galleryNext) {
      galleryNext.addEventListener('click', function () { showPhoto(galleryAt + 1); });
    }

    /* Left/right arrow keys flip photos too -- but never while the caret is in
       a field. Search is a text input on every page of this site, and arrowing
       through a query must not swap the cover behind it. */
    document.addEventListener('keydown', function (e) {
      var el = e.target;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) { return; }
      if (e.key === 'ArrowLeft') { showPhoto(galleryAt - 1); }
      if (e.key === 'ArrowRight') { showPhoto(galleryAt + 1); }
    });
  }

  /* ── Predictive search: group the results by run ───────────────────
     Two engines, deliberately, because each covers the other's blind spot.

     /search?…&view=suggest is the real catalogue search running through a
     paginated template, so it reaches far past the 24 results the normal
     search page shows. It has NO typo tolerance: "badman" returns nothing
     there, ever.

     /search/suggest.json runs on the predictive engine, which forgives one
     wrong letter (or two transposed) once the first four characters are
     right. It only ever returns a flat handful, ranked its own way, and it
     is the ONLY place a shopper recovers a misspelling.

     So both fire, the catalogue view wins, and the predictive results are
     used when the catalogue finds nothing. Nothing that worked before stops
     working; grouping is added on top.

     Grouping is the point. Measured against live on 2026-08-25: "spider man*"
     returns 96 products over four pages, and the four books this shop is
     proudest of — The Amazing Spider-Man #4, #7, #10 and #16 — rank in the
     LAST page, positions ~73-96, behind two hundred later issues of the same
     run. Nobody scrolls four pages of a dropdown-less result list to find
     them. Pulling two pages of 50, grouping by run and sorting each run by
     issue number ascending puts them at the top of their own group, where a
     shopper who types "spider man" actually sees them.

     Two pages, not one: at 50 a page the keys sit past the cut. Measured, not
     assumed — one page ends at 50 and they start at 73. */

  var ISSUE_RE = /(\d+(?:\.\d+)?)/;

  // "4" -> 4, "16 (VG)" -> 16, "" -> Infinity so untitled-issue books sort last
  // rather than jumping the queue ahead of #1.
  function issueNum(s) {
    var m = ISSUE_RE.exec(s || '');
    return m ? parseFloat(m[1]) : Infinity;
  }

  /* ── Every word is a search term ───────────────────────────────────
     The one place the query rule lives; the form submit and the dropdown both
     call it. It used to be two copies, and only the LAST term ever got a star.

     Measured against live 2026-08-26 (tools/search/out/term-probe.txt):

       typed "ama spid" -> sent "ama spid*"   ->   0 results
       typed "ama spid" -> sent "ama* spid*"  ->  94 results

     So abbreviating any word except the last fell off a cliff. Every
     alphabetic term now carries its own star.

     Punctuation is a word boundary, not a character. "spider-man" and
     "spider man" have to reach the same books, and "#4" has to mean 4. The
     apostrophe is kept rather than split on, so World's Finest stays one term.

     Pure numbers are left EXACT, deliberately, and this is the one place the
     change NARROWS. Measured the same day: "spider man 4" sent as
     "spider* man* 4" returns 6 books — the ones actually numbered #4. Sent as
     "spider man 4*" it returns 72, because 4* also matches #40..#49 and #400.
     An issue number is the one term a shopper means literally.

     Quoted phrases are returned untouched, so power users keep that control.

     TITLE-SCOPED SINCE 2026-10-07 (SITE-AUDIT S-1). By October an unscoped
     "word*" matched the WHOLE catalogue: every search said "1000 results" and
     nonsense like "zzqqxx*" returned random books, so the empty state could
     never appear. Measured on live 2026-10-07:

       x* men*            -> 1000      title:x* title:men*          ->  4 (all X-Men)
       zzqqxx*            -> 1000      title:zzqqxx*                ->  0
       spider* man* 4     -> 1000      title:spider* title:man* title:4 -> 3

     So every word now goes out as title:WORD*, every number as title:N (still
     exact), and a decimal grade such as 9.4 stays ONE term instead of being cut
     into 9 and 4 (S-3). Field terms the shopper or a tag box already wrote
     (tag:dc, title:x*) and the OR operator pass through as they are, which is
     also what keeps this idempotent over a URL's own q.

     STOPWORDS ARE DROPPED from the title terms, measured, not guessed: on live
     title:the, title:of, title:a and title:and each return 0, and one of them
     turns the whole query to 0 ("title:the* title:avengers*" -> 0, without the
     the -> 189; "Tales of Suspense" likewise 0 -> 3).

     Creator and grade words ("kirby", "cgc 9.4") are not in titles. When the
     title query finds nothing, the results page re-runs the PLAIN words
     (plainQuery, below; templates/search.liquid does the same in Liquid), and
     so does the dropdown. Plain words without a star are precise on this
     store: kirby -> 144, "cgc 9.4" -> 12, zzqqxx -> 0, "ama spid" -> 79. */
  var TERM_SPLIT = /[^0-9A-Za-z*']+/g;
  var NUMERIC_RE = /^[0-9]+(\.[0-9]+)?$/;
  var WORD_SPLIT = /[^0-9A-Za-z*'.]+/g;
  var FIELD_RE = /^(title|tag|vendor|product_type):\S/i;
  var STOPWORDS = ['a', 'an', 'and', 'at', 'by', 'for', 'from', 'in', 'is', 'of', 'on', 'or', 'the', 'to', 'with'];

  function expandQuery(q) {
    q = (q || '').trim();
    if (!q || q.indexOf('"') !== -1) { return q; }
    var out = [];
    q.split(/\s+/).forEach(function (chunk) {
      if (FIELD_RE.test(chunk) || chunk === 'OR') { out.push(chunk); return; }
      chunk.replace(WORD_SPLIT, ' ').trim().split(/\s+/).forEach(function (w) {
        // A point belongs to a number ("9.4"); anywhere else it is punctuation.
        var parts = NUMERIC_RE.test(w) ? [w] : w.split('.');
        parts.forEach(function (t) {
          if (!t || t === '*') { return; }
          if (STOPWORDS.indexOf(t.replace(/\*$/, '').toLowerCase()) !== -1) { return; }
          if (NUMERIC_RE.test(t) || t.charAt(t.length - 1) === '*') { out.push('title:' + t); }
          else { out.push('title:' + t + '*'); }
        });
      });
    });
    // A query of nothing but punctuation or stopwords would otherwise become the
    // empty string and silently search for everything.
    return out.length ? out.join(' ') : q;
  }

  // The fallback: the words as a person would read them, with no field scope and
  // no star (an unscoped star matches the whole catalogue). Field terms such as
  // tag:dc are kept, so a narrowing survives the fallback.
  function plainQuery(q) {
    return (q || '').replace(/\btitle:/gi, '').replace(/\*/g, '').replace(/\s+/g, ' ').trim();
  }

  // Widening fallback. Fires ONLY when the every-word query found nothing at
  // all — matching every word is what a shopper means, and OR would otherwise
  // bury the right book under everything sharing one common word. Measured
  // 2026-08-26: "aven* spid*" -> 3 results, "aven* OR spid*" -> 211.
  // Returns '' for a single-term query, where OR would mean nothing.
  function orQuery(q) {
    var terms = expandQuery(q).split(/\s+/);
    return terms.length > 1 ? terms.join(' OR ') : '';
  }

  // Recommended searches. These are the run names the catalogue ACTUALLY
  // returned for what was typed — never a fixed list — so a chip cannot point
  // at something this shop does not have. The run the shopper is already
  // looking at is dropped: recommending the search they just ran is noise.
  // Run sizes are counted over the rows we actually fetched — two pages of 50.
  // When the result set is larger than that, the number is a floor, not a total,
  // and it says so. A bare count there would be a claim about the catalogue that
  // nothing measured.
  function runCount(n, partial) {
    return partial ? n + '+' : String(n);
  }

  function normTerm(x) {
    return (x || '').toLowerCase().replace(TERM_SPLIT, ' ').trim();
  }

  function relatedTerms(groups, typed) {
    // Both sides go through the same normalisation or the comparison misses the
    // exact case it exists for: typing "amazing spider-man" against a run named
    // "Amazing Spider-Man" would keep recommending the search just run.
    var typedKey = normTerm(typed);
    var seen = {}, out = [];
    groups.forEach(function (g) {
      var k = normTerm(g.series);
      if (!g.series || seen[k] || k === typedKey) { return; }
      seen[k] = 1;
      out.push(g.series);
    });
    return out.slice(0, 8);
  }

  // One builder for the chip row, used by the dropdown and by the results
  // page, so the two cannot render "recommended searches" differently.
  function termsRow(terms, label) {
    var nav = document.createElement('nav');
    nav.className = 'search-terms';
    nav.setAttribute('aria-label', 'Recommended searches');
    var lab = document.createElement('span');
    lab.className = 'search-terms__label';
    lab.textContent = label || 'Try';
    nav.appendChild(lab);
    var ul = document.createElement('ul');
    ul.className = 'search-terms__list';
    terms.forEach(function (t) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className = 'search-terms__chip';
      a.href = '/search?q=' + encodeURIComponent(expandQuery(t)) + '&type=product';
      a.textContent = t;
      li.appendChild(a);
      ul.appendChild(li);
    });
    nav.appendChild(ul);
    return nav;
  }

  function parseSuggestRows(text) {
    var rows = [];
    var total = 0;
    (text || '').split('\n').forEach(function (line) {
      if (line.charAt(0) === '!') { total = parseInt(line.slice(1), 10) || 0; return; }
      var f = line.split('\t');
      if (f.length < 4) { return; }
      var url = f[3].trim();
      if (!url) { return; }
      rows.push({
        series: f[0].trim(),
        issue: f[1].trim(),
        title: f[2].trim(),
        url: url,
        img: (f[4] || '').trim()
      });
    });
    return { rows: rows, total: total };
  }

  // G6: the title rule search.liquid uses for data-title-hit, for the series bar.
  function titleWordsMatch(series, words) {
    if (!words.length) { return false; }
    var sw = String(series || '').toLowerCase().replace(/[‘’']/g, '')
      .replace(/[-.,:!?()&\/"]+/g, ' ').split(/\s+/).filter(Boolean);
    return words.every(function (w) {
      return sw.some(function (x) { return x.indexOf(w) === 0; });
    });
  }

  function groupRuns(items) {
    var order = [], map = {};
    items.forEach(function (it) {
      var key = it.series.toLowerCase();
      if (!map[key]) { map[key] = { series: it.series, items: [] }; order.push(key); }
      // Two pages can overlap if the catalogue changes between the two
      // requests; a book shown twice in one dropdown looks broken.
      var dupe = map[key].items.some(function (x) { return x.url === it.url; });
      if (!dupe) { map[key].items.push(it); }
    });
    var groups = order.map(function (k) { return map[k]; });
    groups.forEach(function (g) {
      g.items.sort(function (a, b) { return issueNum(a.issue) - issueNum(b.issue); });
    });
    // Biggest run first: if someone types "spider man", the run they meant is
    // the one with the most books in the result set.
    groups.sort(function (a, b) { return b.items.length - a.items.length; });
    return groups;
  }

  /* ── Mobile: search opens as a full screen ─────────────────────────
     On a phone the dropdown was a small panel hanging off a small field, over
     the top of the nav. At this width search gets the whole screen instead:
     the field moves to the top, the runs fill the space below it, and nothing
     is competing for the same pixels.

     900px is not a new breakpoint — it is the theme's own. At exactly that
     width the nav already collapses to the hamburger and .top-bar re-flows the
     search field onto its own row. Inventing a second mobile threshold here
     would mean two definitions of "mobile" that could drift apart.

     THE FORM IS MOVED, NOT REBUILT. The real <form> and the real suggestion
     list are relocated into the overlay and put back on close, so there is one
     search implementation on the site: same input, same submit handler, same
     renderGroups. A second copy rendered "for mobile" is how two search bars
     start behaving differently.

     Nothing here is load-bearing. With JavaScript off the overlay never exists
     and the form is an ordinary GET form in the header, exactly as today. */

  var OVERLAY_MQ = '(max-width: 900px)';
  var overlayEl = null, overlaySlot = null, overlayBody = null, overlayIdleBox = null;
  var overlayFooter = null;
  var overlayForm = null, overlayList = null, overlayAnchor = null;
  var suppressOverlayOpen = false;

  function isMobileSearch() {
    return window.matchMedia(OVERLAY_MQ).matches;
  }

  function ensureOverlay() {
    if (overlayEl) { return; }
    overlayEl = document.createElement('div');
    overlayEl.className = 'search-overlay';
    overlayEl.hidden = true;

    var top = document.createElement('div');
    top.className = 'search-overlay__top';
    overlaySlot = document.createElement('div');
    overlaySlot.className = 'search-overlay__slot';
    top.appendChild(overlaySlot);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'search-overlay__close';
    closeBtn.setAttribute('aria-label', 'Close search');
    closeBtn.textContent = '✕';
    closeBtn.addEventListener('click', function () { dismissOverlay(); });
    top.appendChild(closeBtn);
    overlayEl.appendChild(top);

    overlayBody = document.createElement('div');
    overlayBody.className = 'search-overlay__body';
    overlayIdleBox = document.createElement('div');
    overlayIdleBox.className = 'search-overlay__idle';
    overlayBody.appendChild(overlayIdleBox);

    /* Clear-search, at the END of the scroll. On a phone the results run past
       the fold, and once a shopper has scrolled through a run the search box is
       far above them — reaching back up to empty it by hand is the awkward part.
       This sits where they actually stop reading. It clears rather than closes:
       clearing returns the popular chips, so the next search starts from
       somewhere instead of from a blank screen. */
    overlayFooter = document.createElement('div');
    overlayFooter.className = 'search-overlay__footer';
    overlayFooter.hidden = true;
    var clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'search-overlay__clear';
    clearBtn.textContent = 'Clear search';
    clearBtn.addEventListener('click', function () {
      if (!overlayForm) { return; }
      var i = overlayForm.querySelector('[data-search-input]');
      if (!i) { return; }
      i.value = '';
      i.dispatchEvent(new Event('input', { bubbles: true }));
      overlaySetFooter(false);
      // Focus goes back to the field, not to <body>, or the next tap starts from
      // the top of the overlay.
      i.focus();
    });
    overlayFooter.appendChild(clearBtn);
    overlayBody.appendChild(overlayFooter);
    overlayEl.appendChild(overlayBody);

    document.body.appendChild(overlayEl);
  }

  function overlaySetFooter(show) {
    if (overlayFooter) { overlayFooter.hidden = !show; }
  }

  /* Empty box: show the popular-search chips rather than a blank screen. They
     are CLONED out of the inert <template> snippets/header.liquid renders, so
     the term list stays in the one Liquid file that owns it and JavaScript
     never holds a copy of it. If the template is absent the overlay simply
     opens empty — which is what it did before, not a break. */
  function overlayIdleFill() {
    if (!overlayIdleBox || overlayIdleBox.firstChild) { return; }
    var tpl = document.querySelector('template[data-popular-searches]');
    if (tpl && 'content' in tpl) {
      overlayIdleBox.appendChild(tpl.content.cloneNode(true));
    }
  }

  function overlayIdleClear() {
    if (overlayIdleBox) { overlayIdleBox.innerHTML = ''; }
  }

  function openOverlay(form, list) {
    ensureOverlay();
    if (overlayForm) { return; }
    overlayForm = form;
    overlayList = list;
    // A comment node marks exactly where the form came from. Remembering the
    // parent alone is not enough — the header form has siblings on both sides,
    // and putting it back at the end would move it past them.
    overlayAnchor = document.createComment('search-form');
    form.parentNode.insertBefore(overlayAnchor, form);
    overlaySlot.appendChild(form);
    // Before the footer, never after it — the clear control is meant to be the
    // last thing at the bottom of the scroll.
    overlayBody.insertBefore(list, overlayFooter);
    overlaySetFooter((form.querySelector('[data-search-input]') || {}).value ? true : false);
    overlayEl.hidden = false;
    document.documentElement.classList.add('search-overlay-open');
    if (list.hidden) { overlayIdleFill(); } else { overlayIdleClear(); }
    // Moving a focused node blurs it, so the first tap used to leave focus on
    // <body> and typing went nowhere until a second tap (SITE-AUDIT M-1). Put
    // focus back in the SAME tap gesture, which is what lets iOS keep the
    // keyboard up. The flag stops this focus() re-entering openOverlay.
    var inp = form.querySelector('[data-search-input]');
    if (inp && document.activeElement !== inp) {
      suppressOverlayOpen = true;
      try { inp.focus({ preventScroll: true }); } catch (err) { inp.focus(); }
      suppressOverlayOpen = false;
    }
  }

  function closeOverlay() {
    if (!overlayForm) { return null; }
    var form = overlayForm, list = overlayList;
    overlayAnchor.parentNode.insertBefore(form, overlayAnchor);
    overlayAnchor.parentNode.removeChild(overlayAnchor);
    // The list goes back to being the form's own absolutely-positioned dropdown,
    // emptied — leaving results behind would make them reappear under the header
    // the moment the viewport widened past the breakpoint.
    form.appendChild(list);
    list.hidden = true;
    list.innerHTML = '';
    form.classList.remove('is-suggesting');
    overlayIdleClear();
    overlaySetFooter(false);
    overlayEl.hidden = true;
    document.documentElement.classList.remove('search-overlay-open');
    overlayForm = null; overlayList = null; overlayAnchor = null;
    return form;
  }

  // Close and hand focus back to the field the shopper started from. The
  // suppression flag matters: focus() fires the same listener that opens the
  // overlay, so without it closing would immediately re-open.
  function dismissOverlay() {
    var form = closeOverlay();
    if (!form) { return; }
    var input = form.querySelector('[data-search-input]');
    if (!input) { return; }
    suppressOverlayOpen = true;
    input.focus();
    suppressOverlayOpen = false;
  }

  // Crossing the breakpoint with the overlay open would strand the form inside a
  // hidden container, so it is put back before that can happen.
  window.addEventListener('resize', function () {
    if (overlayForm && !isMobileSearch()) { closeOverlay(); }
  });

  document.querySelectorAll('form[role="search"]').forEach(function (form) {
    var input = form.querySelector('[data-search-input]');
    if (!input) { return; }
    var list = document.createElement('ul');
    list.className = 'search-suggest';
    list.hidden = true;
    form.appendChild(list);

    var timer = null;
    var controller = null;

    function close() {
      list.hidden = true;
      list.innerHTML = '';
      form.classList.remove('is-suggesting');
      // In the overlay an empty list means a blank screen, so the popular-search
      // chips take its place rather than nothing at all.
      if (overlayForm === form) { overlayIdleFill(); }
    }

    // Tapping the field at phone width hands search the whole screen. Both events
    // are bound: focus covers keyboard and assistive tabbing, click covers a tap
    // on an input that already had focus.
    function maybeOpenOverlay() {
      if (suppressOverlayOpen || !isMobileSearch()) { return; }
      openOverlay(form, list);
    }
    input.addEventListener('focus', maybeOpenOverlay);
    input.addEventListener('click', maybeOpenOverlay);
    input.addEventListener('input', function () {
      if (overlayForm === form) { overlaySetFooter(input.value.trim().length > 0); }
    });

    function issueLink(it) {
      var a = document.createElement('a');
      a.className = 'search-suggest__issue';
      a.href = it.url;
      if (it.img) {
        var im = document.createElement('img');
        im.src = it.img;
        im.alt = '';
        im.loading = 'lazy';
        im.width = 28;
        im.height = 42;
        a.appendChild(im);
      }
      var label = document.createElement('span');
      // Inside a run header the series name is already on screen, so the issue
      // number alone is the useful part. A book with no "#" in its title has
      // nothing to shorten to, so it keeps its full title.
      label.textContent = it.issue ? '#' + it.issue : it.title;
      a.appendChild(label);
      return a;
    }

    // `partial` is derived from ROWS FETCHED against the reported total, never
    // from the deduped group sizes — the dropdown legitimately shows 87 books for
    // a 96-result search (the live results page serves 9 of them twice), and
    // reading that gap as "we have not seen everything" would put a "+" on counts
    // that are exact.
    function renderGroups(groups, total, note, partial) {
      list.innerHTML = '';
      var shown = groups.slice(0, 4);
      // The full-screen overlay has a screen to fill, not a strip under a header,
      // so it shows roughly twice as much of each run. Same renderer, one number.
      var roomy = overlayForm === form;
      var perRun = shown.length === 1 ? (roomy ? 12 : 6) : (roomy ? 6 : 3);

      // Honest first line when the results are NOT what was asked for. The OR
      // fallback returns books matching one word out of several, and showing
      // those silently under the shopper's own query would misreport them.
      if (note) {
        var noteLi = document.createElement('li');
        noteLi.className = 'search-suggest__note';
        noteLi.textContent = note;
        list.appendChild(noteLi);
      }

      // Recommended searches, from the runs this query actually returned.
      // Above the results rather than below: it is a way to narrow a broad
      // query, which is only useful before the shopper starts scanning.
      var terms = relatedTerms(groups, input.value.trim());
      if (terms.length > 1) {
        var tli = document.createElement('li');
        tli.className = 'search-suggest__terms';
        tli.appendChild(termsRow(terms, 'Try'));
        list.appendChild(tli);
      }

      shown.forEach(function (g) {
        var li = document.createElement('li');
        li.className = 'search-suggest__run';

        var head = document.createElement('a');
        head.className = 'search-suggest__runhead';
        head.href = '/search?q=' + encodeURIComponent(expandQuery(g.series)) + '&type=product';
        var name = document.createElement('span');
        name.className = 'search-suggest__runname';
        name.textContent = g.series;
        head.appendChild(name);
        var count = document.createElement('span');
        count.className = 'search-suggest__runcount';
        count.textContent = runCount(g.items.length, partial) +
                            (g.items.length === 1 && !partial ? ' book' : ' books');
        head.appendChild(count);
        li.appendChild(head);

        var issues = document.createElement('ul');
        issues.className = 'search-suggest__issues';
        g.items.slice(0, perRun).forEach(function (it) {
          var ili = document.createElement('li');
          ili.appendChild(issueLink(it));
          issues.appendChild(ili);
        });
        li.appendChild(issues);
        list.appendChild(li);
      });

      // Only offer "see all" when there is genuinely more behind it, so the
      // row never lies about a result set the dropdown already showed whole.
      var listed = shown.reduce(function (n, g) { return n + Math.min(g.items.length, perRun); }, 0);
      if (total > listed) {
        var more = document.createElement('li');
        more.className = 'search-suggest__all';
        var a = document.createElement('a');
        a.href = '/search?q=' + encodeURIComponent(expandQuery(input.value.trim())) + '&type=product';
        a.textContent = 'See all ' + total + ' results';
        more.appendChild(a);
        list.appendChild(more);
      }

      list.hidden = false;
      form.classList.add('is-suggesting');
      if (overlayForm === form) { overlayIdleClear(); }
    }

    // Predictive-engine results have no run structure, so each one becomes its
    // own single-book group. Same markup, no special case downstream.
    function renderFlat(products) {
      renderGroups(products.map(function (p) {
        return { series: p.title, items: [{ issue: '', title: p.title, url: p.url, img: p.image || '' }] };
      }), products.length, null, false);
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (timer) { clearTimeout(timer); }
      if (q.length < 2) {
        if (controller) { controller.abort(); controller = null; }
        close();
        return;
      }
      timer = setTimeout(function () {
        if (controller) { controller.abort(); }
        controller = typeof AbortController === 'function' ? new AbortController() : null;
        var opts = controller ? { signal: controller.signal } : undefined;
        var stale = function () { return input.value.trim() !== q; };
        var wq = encodeURIComponent(expandQuery(q));

        function page(n) {
          return fetch('/search?q=' + wq + '&type=product&view=suggest&page=' + n, opts)
            .then(function (r) { return r.ok ? r.text() : ''; })
            .catch(function () { return ''; });
        }

        Promise.all([
          page(1),
          page(2),
          fetch('/search/suggest.json?q=' + encodeURIComponent(q) +
                '&resources[type]=product&resources[limit]=6', opts)
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; })
        ]).then(function (res) {
          var a = parseSuggestRows(res[0]);
          var b = parseSuggestRows(res[1]);
          var rows = a.rows.concat(b.rows);
          if (rows.length) {
            var t = a.total || rows.length;
            if (stale()) { return; }
            renderGroups(groupRuns(rows), t, null, rows.length < t);
            return;
          }

          // No TITLE matches every word. Three recoveries, in order of how
          // close they stay to what was typed:
          //   1. the same title words joined by OR — a real word the shop simply
          //      does not pair with the others ("spider batman" -> 0, OR -> 83);
          //   2. the plain words, unscoped — creators, grades and abbreviations
          //      live in descriptions, not titles ("kirby" -> 144, "ama spid" ->
          //      79). The results page falls back the same way, so "See all"
          //      lands on the same books;
          //   3. the predictive engine — a misspelling, which neither can fix.
          var d = res[2];
          var products = (d && d.resources && d.resources.results && d.resources.results.products) || [];
          function predictive() {
            if (stale()) { return; }
            if (products.length) { renderFlat(products); } else { close(); }
          }
          function widen(query, note, next) {
            if (!query) { next(); return; }
            fetch('/search?q=' + encodeURIComponent(query) + '&type=product&view=suggest', opts)
              .then(function (r) { return r.ok ? r.text() : ''; })
              .catch(function () { return ''; })
              .then(function (text) {
                var w = parseSuggestRows(text);
                if (!w.rows.length) { next(); return; }
                var wt = w.total || w.rows.length;
                if (stale()) { return; }
                renderGroups(groupRuns(w.rows), wt, note, w.rows.length < wt);
              });
          }
          var pq = plainQuery(q);
          if (pq === expandQuery(q)) { pq = ''; }
          widen(orQuery(q), 'Nothing matches every word. Showing books that match one:', function () {
            widen(pq, 'No title matches. Showing books that mention it:', predictive);
          });
        });
      }, 250);
    });

    form.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        // In the overlay, Escape means "put the page back", not "empty the list" —
        // clearing the results would leave a full screen showing nothing.
        if (overlayForm === form) { dismissOverlay(); return; }
        // If focus is on a suggestion, closing the list would destroy the focused
        // element and dump focus on <body> — the next Tab would restart from the
        // top of the page. Hand focus back to the input first.
        var insideList = list.contains(document.activeElement);
        close();
        if (insideList) { input.focus(); }
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') { return; }
      var items = Array.prototype.slice.call(list.querySelectorAll('a'));
      if (list.hidden || !items.length) { return; }
      e.preventDefault();
      var i = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { i = i < 0 ? 0 : Math.min(i + 1, items.length - 1); }
      else if (i <= 0) { input.focus(); return; }
      else { i = i - 1; }
      items[i].focus();
    });

    document.addEventListener('click', function (e) {
      // The overlay has its own close control. Letting "clicked outside the form"
      // fire in there would blank the results and leave an empty full screen.
      if (overlayForm === form) { return; }
      if (!form.contains(e.target)) { close(); }
    });
  });

  /* ── "More Like This": rank the run ────────────────────────────────
     Liquid has already emitted a correctly-tiered row (another copy of this
     issue, then the rest of the run, then fill) from the collections this book
     belongs to. Two things are still left to do here.

     1. Go wider. A Liquid collection loop stops at 50 and {% paginate %} does
        not exist on a product template, so a long run can be cut off before
        its own issues are reached. The search template IS paginated, so
        /search?...&view=cards returns real product-card markup for the whole
        catalogue. Same snippet, same price-block — nothing about a card is
        rebuilt in JavaScript, so the discount display cannot drift.
     2. Order the run properly. Issues nearest the one being viewed come first:
        from #16 you want #15 and #17, not #1 and #104.

     Everything here is an upgrade on top of a row that already works. If the
     fetch fails, the Liquid order stands. */
  (function () {
    var grid = document.querySelector('[data-related-grid]');
    if (!grid || typeof fetch !== 'function' || typeof DOMParser !== 'function') { return; }

    var SHOW = 4;
    // Must match the Liquid normalisation in product-card.liquid / product.liquid
    // exactly: a leading "The " is not part of run identity. If these two ever
    // disagree, the server and the client sort the same row differently.
    var strip = function (x) { return (x || '').replace(/^The /, ''); };
    var series = strip(grid.getAttribute('data-series'));
    var handle = grid.getAttribute('data-handle') || '';
    var issue = parseInt(grid.getAttribute('data-issue'), 10);
    var copy = grid.getAttribute('data-copy') || '';
    if (!series) { return; }

    /* Another copy = same run, same issue, DIFFERENT copy number. Same issue
       alone is not enough: two listings of a single book look identical that
       way, and the store has exactly that case. A missing copy number on
       either side is treated as "can't tell" and allowed, so a real second
       copy is never hidden by an incomplete SKU. */
    function tierOf(el) {
      if (strip(el.getAttribute('data-series')) !== series) { return 2; }
      var i = parseInt(el.getAttribute('data-issue'), 10);
      if (isNaN(issue) || isNaN(i) || i !== issue) { return 1; }
      var c = el.getAttribute('data-copy') || '';
      return (!c || !copy || c !== copy) ? 0 : 1;
    }

    function distance(el) {
      var i = parseInt(el.getAttribute('data-issue'), 10);
      if (isNaN(i) || isNaN(issue)) { return Number.MAX_SAFE_INTEGER; }
      return Math.abs(i - issue);
    }

    function markCopy(el) {
      if (el.querySelector('.badge--copy')) { return; }
      var badges = el.querySelector('.product-card__badges');
      if (!badges) { return; }
      var span = document.createElement('span');
      span.className = 'badge badge--copy';
      span.textContent = 'Another Copy';
      badges.insertBefore(span, badges.firstChild);
    }

    function apply(cards) {
      var seen = Object.create(null);
      if (handle) { seen[handle] = true; }
      var keep = [];
      cards.forEach(function (el) {
        var h = el.getAttribute('data-handle');
        if (!h || seen[h]) { return; }
        seen[h] = true;
        keep.push(el);
      });

      /* Decorate before sorting — tier 2 gets shuffled, and a stable sort on a
         constant key would otherwise leave the fill in DOM order every time. */
      keep.forEach(function (el, idx) {
        el.__tier = tierOf(el);
        el.__dist = distance(el);
        el.__order = idx;
        el.__rand = Math.random();
      });

      keep.sort(function (a, b) {
        if (a.__tier !== b.__tier) { return a.__tier - b.__tier; }
        if (a.__tier === 2) { return a.__rand - b.__rand; }
        if (a.__dist !== b.__dist) { return a.__dist - b.__dist; }
        return a.__order - b.__order;
      });

      var final = keep.slice(0, SHOW);
      final.forEach(function (el) {
        if (el.__tier === 0) { markCopy(el); }
        grid.appendChild(el);
      });
      Array.prototype.slice.call(grid.children).forEach(function (el) {
        if (final.indexOf(el) === -1) { el.parentNode.removeChild(el); }
      });
    }

    var existing = Array.prototype.slice.call(grid.querySelectorAll('.product-card'));

    /* Parentheticals are part of the run's identity ("Wonder Woman (1st
       Series)") but they are punctuation to the search engine, so they come
       out of the QUERY and stay in the equality check below. */
    var q = series.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();
    if (!q) { apply(existing); return; }

    fetch('/search?q=' + encodeURIComponent(q) + '&type=product&view=cards', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var found = Array.prototype.slice.call(doc.querySelectorAll('.product-card[data-handle]'));
        var extra = found.filter(function (el) {
          /* Search is fuzzy by design — "Weird Fantasy" also returns
             "Weird Science-Fantasy #28". Only exact run matches are worth
             importing; fill is already covered by Liquid. */
          return strip(el.getAttribute('data-series')) === series;
        }).map(function (el) { return document.importNode(el, true); });
        apply(existing.concat(extra));
      })
      .catch(function () { apply(existing); });
  })();

  /* ── Search results page: the run dock ─────────────────────────────
     The complaint this exists for, in the owner's words: "when you serch
     spider man the amazing spider man doesnt show up."

     It does show up. Measured on live 2026-08-26, q="spider man*" returns 96
     products over four pages of 24, and the three books titled "The Amazing
     Spider-Man" sit at positions 89, 90 and 91 — page four. Cause, measured
     off the catalogue rather than guessed: 84 books are titled "Amazing
     Spider-Man #nnn" and 4 are titled "The Amazing Spider-Man #n", and
     Shopify's relevance engine treats those as unrelated series. The owner
     ruled the titles stay ("dont rename the titles just upgrade the serch
     bar"), so the results page does what the dropdown does: strip a leading
     "The " for run identity, group, sort each run by issue ascending.

     It sits ABOVE the filters row, full width, and its bar collapses it to a
     single line — the owner's own design, chosen 2026-08-26. The collapsed
     line is the point: it keeps the series and their counts as links, so
     folding it away costs the covers and not the information. A bar that just
     said "Search suggestions" would be a heading, not a control.

     This is an addition ABOVE the normal grid, never a replacement. The grid
     is server-rendered and paginated and stays exactly as it was, so with JS
     off, or if this fetch fails, nothing is lost. Nothing here is load-bearing.

     Same feed as the dropdown (view=suggest, two pages of 50), so there is one
     grouping implementation on the site and it cannot drift. */
  (function () {
    var mount = document.querySelector('[data-search-runs]');
    if (!mount) { return; }
    var q = mount.getAttribute('data-query') || '';
    if (!q) { return; }
    // Send the query the page was served with, VERBATIM. It is normally the
    // expanded title form already; when the page is the plain-words fallback
    // (templates/search.liquid, SITE-AUDIT S-1) re-expanding it would scope it
    // back to titles and the dock would disagree with the grid under it.
    var wq = encodeURIComponent(q);

    function page(n) {
      return fetch('/search?q=' + wq + '&type=product&view=suggest&page=' + n, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    }

    function issueLink(it) {
      var a = document.createElement('a');
      a.className = 'search-runs__issue';
      a.href = it.url;
      if (it.img) {
        var im = document.createElement('img');
        im.src = it.img; im.alt = ''; im.loading = 'lazy';
        im.width = 34; im.height = 51;
        a.appendChild(im);
      }
      var label = document.createElement('span');
      label.textContent = it.issue ? '#' + it.issue : it.title;
      a.appendChild(label);
      return a;
    }

    Promise.all([page(1), page(2)]).then(function (res) {
      var a = parseSuggestRows(res[0]);
      var rows = a.rows.concat(parseSuggestRows(res[1]).rows);
      if (!rows.length) { return; }

      var groups = groupRuns(rows);
      // G6 (2026-10-08, Bruce: "only show that title"): when the grid below is title-only
      // (search.liquid data-title-only), the bar lists only the series that match the same
      // words by the same rule (each word starts a word of the series; "The" and the small
      // words were already left out of data-title-words). No title match: today's bar.
      var titleGrid = document.querySelector('[data-search-sort][data-title-only]');
      if (titleGrid) {
        var tw = (titleGrid.getAttribute('data-title-words') || '').split(' ').filter(Boolean);
        groups = groups.filter(function (g) { return titleWordsMatch(g.series, tw); });
      }
      // rows stays the whole two-page pull, so "partial" (and the "+" on a run's count) still
      // says whether the feed held more than was fetched.
      var total = a.total || rows.length;
      // Two pages of 50 is all we ever pull. Past that the per-run numbers count
      // what we SAW, not what exists, and printing them bare would be a claim
      // about the catalogue that nothing measured. runCount() marks them.
      var partial = rows.length < total;

      // One series and nothing else is the case where this earns nothing: the
      // grid below already shows that run, in order, on page one.
      if (groups.length < 2) { return; }

      var dock = document.createElement('div');
      dock.className = 'search-runs__dock';

      var line = document.createElement('div');
      line.className = 'search-runs__line';

      var count = document.createElement('span');
      count.className = 'search-runs__count';
      count.textContent = groups.length + ' series';
      line.appendChild(count);

      var sep = document.createElement('span');
      sep.className = 'search-runs__sep';
      sep.setAttribute('aria-hidden', 'true');
      sep.textContent = '/';
      line.appendChild(sep);

      // The series are real links, not labels, so the collapsed line still
      // navigates. That is why the bar is not itself a <button> — a link inside
      // a button is invalid and unreachable by keyboard.
      var seriesList = document.createElement('ul');
      seriesList.className = 'search-runs__serieslist';
      groups.slice(0, 6).forEach(function (g) {
        var li = document.createElement('li');
        var link = document.createElement('a');
        link.className = 'search-runs__series';
        link.href = '/search?q=' + encodeURIComponent(expandQuery(g.series)) + '&type=product';
        link.appendChild(document.createTextNode(g.series + ' '));
        var n = document.createElement('b');
        n.textContent = runCount(g.items.length, partial);
        link.appendChild(n);
        li.appendChild(link);
        seriesList.appendChild(li);
      });
      line.appendChild(seriesList);

      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'search-runs__toggle';
      toggle.setAttribute('aria-controls', 'search-runs-body');
      line.appendChild(toggle);
      dock.appendChild(line);

      var body = document.createElement('div');
      body.className = 'search-runs__body';
      body.id = 'search-runs-body';
      var pad = document.createElement('div');
      pad.className = 'search-runs__pad';
      var list = document.createElement('ul');
      list.className = 'search-runs__list';

      groups.slice(0, 6).forEach(function (g) {
        var li = document.createElement('li');
        li.className = 'search-runs__run';

        var head = document.createElement('a');
        head.className = 'search-runs__runhead';
        head.href = '/search?q=' + encodeURIComponent(expandQuery(g.series)) + '&type=product';
        var name = document.createElement('span');
        name.className = 'search-runs__runname';
        name.textContent = g.series;
        head.appendChild(name);
        var c = document.createElement('span');
        c.className = 'search-runs__runcount';
        c.textContent = runCount(g.items.length, partial) +
                        (g.items.length === 1 && !partial ? ' book' : ' books');
        head.appendChild(c);
        li.appendChild(head);

        var issues = document.createElement('ul');
        issues.className = 'search-runs__issues';
        g.items.slice(0, 8).forEach(function (it) {
          var ili = document.createElement('li');
          ili.appendChild(issueLink(it));
          issues.appendChild(ili);
        });
        li.appendChild(issues);
        list.appendChild(li);
      });

      pad.appendChild(list);
      body.appendChild(pad);
      dock.appendChild(body);

      function setCollapsed(collapsed) {
        dock.classList.toggle('is-collapsed', collapsed);
        // Covers hidden by the collapse must leave the Tab order too, or a
        // keyboard user tabs through links they cannot see (SITE-AUDIT S-7).
        body.inert = collapsed;
        if (collapsed) { body.setAttribute('aria-hidden', 'true'); } else { body.removeAttribute('aria-hidden'); }
        toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        toggle.textContent = collapsed ? 'Show covers' : 'Hide covers';
      }
      toggle.addEventListener('click', function () {
        setCollapsed(!dock.classList.contains('is-collapsed'));
      });
      // Clicking the bar itself opens it too — the owner asked for "pressing the
      // top". A click that landed on one of the series links is a navigation, not
      // a toggle, so it is left alone.
      line.addEventListener('click', function (e) {
        if (e.target.closest('a') || e.target === toggle) { return; }
        toggle.click();
      });

      // Leaving the box closes it again. Two guards, both borrowed from the
      // filter panel's facet menus, where the same interaction was already
      // tuned:
      //   * a grace delay, because a diagonal path toward a cover briefly leaves
      //     the box and closing mid-reach feels broken. Re-entering cancels.
      //   * never close out from under the keyboard. A shopper can open it with
      //     the pointer, then Tab into the covers; if the pointer then drifts
      //     off, closing would destroy the focused link and dump focus on <body>.
      // mouseleave never fires on touch, which is correct here — on a phone the
      // whole search is the full-screen overlay and this dock is not what a
      // thumb is using.
      var leaveTimer = null;
      dock.addEventListener('mouseleave', function () {
        if (dock.classList.contains('is-collapsed')) { return; }
        leaveTimer = setTimeout(function () {
          if (dock.contains(document.activeElement)) { return; }
          setCollapsed(true);
        }, 250);
      });
      dock.addEventListener('mouseenter', function () {
        if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
      });

      // Closed by default, showing the series line only — the owner's ruling
      // 2026-08-26. It is not remembered: the box closes itself on mouse-out, so
      // a stored "open" preference would be overwritten seconds later anyway.
      setCollapsed(true);
      mount.appendChild(dock);
      mount.hidden = false;
    });
  })();

  /* ── Price range: two stacked sliders make one dual thumb ──────────
     Moved here out of filter-panel.liquid's inline script when the control was
     extracted to snippets/price-range.liquid, so that /search and /collections
     share one implementation. Keyed off [data-price-form] anywhere on the page.

     THE THUMBS MOVE BETWEEN STOPS, NOT DOLLARS. Their value is an INDEX into the
     stop list the snippet emits on data-price-stops; the prices behind the stops
     are not evenly spaced. Measured on live 2026-08-26: 388 of 549 comics are
     $100 or less and exactly one is over $2,500, so an evenly-spaced 1-to-5,000
     track would spend 98% of its travel on one book. Eleven of the stops sit
     below $100 instead.

     There is NO copy of the stop list here. It lives in the snippet and only in
     the snippet. If the attribute is missing or unparseable this whole block
     bows out and the form still works — the two number boxes are ordinary named
     inputs and submit on their own.

     The number boxes are the ones that carry name=, so what submits is always
     what is typed. They are not restricted to the stops: type $137 and $137 is
     what gets sent, while the thumb parks on the nearest stop. */

  function parsePriceStops(text) {
    var out = [];
    (text || '').split(',').forEach(function (t) {
      var n = parseFloat(t);
      if (!isNaN(n)) { out.push(n); }
    });
    // Ascending is assumed by every index comparison below; a list authored out
    // of order would put the "max" thumb left of the "min" thumb.
    for (var i = 1; i < out.length; i++) {
      if (out[i] <= out[i - 1]) { return []; }
    }
    return out;
  }

  // Nearest by absolute distance, ties going to the LOWER stop — a shopper who
  // types a number between two stops is more often naming a ceiling they do not
  // want to exceed than a floor they want to clear.
  function nearestStopIndex(stops, dollars) {
    var best = 0, bestD = Infinity;
    for (var i = 0; i < stops.length; i++) {
      var d = Math.abs(stops[i] - dollars);
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  }

  document.querySelectorAll('[data-price-form]').forEach(function (priceForm) {
    var lo = priceForm.querySelector('[data-price-thumb="lo"]');
    var hi = priceForm.querySelector('[data-price-thumb="hi"]');
    var loBox = priceForm.querySelector('[data-price-input="lo"]');
    var hiBox = priceForm.querySelector('[data-price-input="hi"]');
    var fill = priceForm.querySelector('[data-price-fill]');
    var readout = priceForm.querySelector('[data-price-readout]');
    if (!lo || !hi || !loBox || !hiBox || !fill) { return; }

    var stops = parsePriceStops(priceForm.getAttribute('data-price-stops'));
    if (stops.length < 2) { return; }
    var last = stops.length - 1;

    function money(n) {
      return n >= 1000 ? n.toLocaleString('en-US') : String(n);
    }

    // The readout reports the BOUNDS THAT WILL SUBMIT, not the stop the thumb is
    // parked on. Those differ whenever a price was typed between two stops, and
    // showing the stop there would put a number on screen that is not the number
    // being sent.
    function paintReadout() {
      if (!readout) { return; }
      var a = loBox.value === '' ? '$' + money(stops[0]) : '$' + money(+loBox.value);
      var b = hiBox.value === '' || +hiBox.value >= stops[last]
        ? '$' + money(stops[last]) + '+'
        : '$' + money(+hiBox.value);
      readout.textContent = a + ' – ' + b;
    }

    function paint() {
      var a = Math.min(+lo.value, +hi.value);
      var b = Math.max(+lo.value, +hi.value);
      fill.style.left = (a / last * 100) + '%';
      fill.style.right = (100 - b / last * 100) + '%';
      // The lower thumb sits on top only while it is at the far right, or it
      // becomes unreachable once the two meet at the ceiling.
      lo.style.zIndex = (+lo.value >= last) ? 4 : 3;
      paintReadout();
    }

    function fromSlider() {
      if (+lo.value > +hi.value) {
        var t = lo.value; lo.value = hi.value; hi.value = t;
      }
      // Empty means "no bound". Both ends collapse to empty at their own extreme
      // so that dragging back to the end clears the filter rather than pinning it
      // to $1 or to $5,000 exactly.
      loBox.value = +lo.value === 0 ? '' : stops[+lo.value];
      hiBox.value = +hi.value >= last ? '' : stops[+hi.value];
      paint();
    }

    function fromBox() {
      lo.value = loBox.value === '' ? 0
        : nearestStopIndex(stops, Math.max(0, +loBox.value));
      hi.value = hiBox.value === '' ? last
        : nearestStopIndex(stops, +hiBox.value);
      paint();
    }

    lo.addEventListener('input', fromSlider);
    hi.addEventListener('input', fromSlider);
    loBox.addEventListener('input', fromBox);
    hiBox.addEventListener('input', fromBox);
    paint();

    priceForm.addEventListener('submit', function () {
      // Empty means "no bound" — submitting filter.v.price.gte= with no value
      // leaves a dead param in the URL and reads as a filter that is on. The top
      // stop is open-ended by design: at or above it, no upper bound is sent at
      // all, which is what makes it "$5,000 and more" rather than "$5,000".
      if (loBox.value === '' || +loBox.value <= 0) { loBox.removeAttribute('name'); }
      if (hiBox.value === '' || +hiBox.value >= stops[last]) { hiBox.removeAttribute('name'); }
      // action="" drops the existing query string, so Just In has to be re-added
      // or applying a price silently turns the sort off.
      if (window.location.search.indexOf('newfirst=1') !== -1 &&
          !priceForm.querySelector('input[name="newfirst"]')) {
        var nf = document.createElement('input');
        nf.type = 'hidden'; nf.name = 'newfirst'; nf.value = '1';
        priceForm.appendChild(nf);
      }
    });
  });

  /* ── Cart count stub (static demo only) ────────────────────────── */
  var cartCount = document.querySelector('[data-cart-count]');
  if (cartCount) {
    var count = parseInt(window.localStorage.getItem('rc-cart-count') || '0', 10);
    if (count > 0) {
      cartCount.textContent = String(count);
      cartCount.hidden = false;
    }
  }

  /* ── Search results: group by run, ascending inside the run ───────
     Centurio's answer, 2026-09-03 11:46: "Parse it. Group by title and then
     ascending within each title. No numbers or variants at the end."

     Shopify's search is relevance-ordered and offers no sort_by, so this
     cannot be done on the server. It is done to the cards the page already
     rendered — product-card.liquid has emitted data-series and data-issue on
     every card since the run-identity work, so there is ONE parse on the site
     and this reuses it rather than re-splitting titles here.

     THE BOUNDARY, stated because it is real: the grid is paginated, so this
     orders a page, not the whole result set. That is why search.liquid now
     paginates by 50 rather than 24 — the largest slice Shopify allows — and
     why the runs dock above the grid stays: the dock reaches two pages of the
     suggest feed and is what carries a long run's low numbers across the page
     break. Neither is load-bearing; with JS off the grid is exactly the
     relevance-ordered grid it has always been.

     Group order is FIRST APPEARANCE, not alphabetical: it keeps Shopify's
     relevance ranking meaningful at the group level, so the run a shopper
     actually typed still leads. Grouping was the ask; re-ranking was not. */
  var VARIANT_RE = /\b(variant|var\.|facsimile|reprint|(2nd|second)\s+print(ing)?)\b/i;

  (function () {
    var grid = document.querySelector('[data-search-sort]');
    if (!grid) { return; }
    var cards = Array.prototype.slice.call(grid.querySelectorAll('.product-card'));
    if (cards.length < 2) { return; }

    var order = [], runs = {}, tail = [];

    cards.forEach(function (card) {
      var issue = card.getAttribute('data-issue') || '';
      // Untitled-issue books and variants both go to the end, his words. A
      // variant of #4 is not #4, so it must not sort into the middle of a run.
      if (!issue || !isFinite(issueNum(issue)) || VARIANT_RE.test(issue)) {
        tail.push(card);
        return;
      }
      var key = (card.getAttribute('data-series') || '').toLowerCase();
      if (!runs[key]) { runs[key] = []; order.push(key); }
      runs[key].push(card);
    });

    // Nothing to group: one run, or every card already in the tail. Reordering
    // would only churn the DOM for no visible change.
    if (!order.length || (order.length === 1 && !tail.length)) { return; }

    // Stable by construction: Array.prototype.sort is stable in every browser
    // that ships this file, so two copies of one issue keep the server's order
    // and the copy numbers stay in sequence.
    order.forEach(function (k) {
      runs[k].sort(function (a, b) {
        return issueNum(a.getAttribute('data-issue')) - issueNum(b.getAttribute('data-issue'));
      });
    });

    var frag = document.createDocumentFragment();
    order.forEach(function (k) {
      runs[k].forEach(function (c) { frag.appendChild(c); });
    });
    tail.forEach(function (c) { frag.appendChild(c); });
    grid.appendChild(frag);
  })();


  /* ── Search results: a series search shows that series and nothing else ──
     Owner, 2026-09-16: "when you serch a series show just the series and then
     no more books below."

     Shopify's search is fuzzy and every word of the query carries a star, so
     "conan" returns the Conan run AND anything else whose text happens to hit.
     When the thing typed IS a series, everything after it is noise sitting
     under the answer.

     WHEN IT ENGAGES is deliberately narrow, because the cost of getting this
     wrong is hiding books a shopper wanted:
       - every word of the query appears in the series name, AND
       - exactly ONE series on the page satisfies that, AND
       - there is something else on the page to hide.
     "comics" is in Detective Comics, Action Comics and Marvel Comics -- three
     series, so that is a keyword search and nothing is hidden. "conan" is in
     Conan the Barbarian alone, so that is a series search and the rest goes.

     The others are HIDDEN, never removed, and one line under the grid says how
     many and puts them back in a click. Silently dropping results someone asked
     for is how a search loses a sale, and it would also make the count above the
     grid a lie with nothing on the page to explain it.

     Server-rendered grid, so with JS off this does nothing and the page is the
     full relevance-ordered result set it has always been. */
  function seriesKey(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[‘’']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\bthe\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  (function () {
    var grid = document.querySelector('[data-search-sort]');
    if (!grid) { return; }
    // G5 (2026-10-08): the server already kept this page to the title typed (search.liquid,
    // data-title-only + data-title-hit, hidden in grid-stand.css) and that rule also covers the
    // pages the infinite scroll appends. A second narrowing here would count hidden cards and
    // offer a "show the others" button that could not show them.
    if (grid.hasAttribute('data-title-only')) { return; }
    var cards = Array.prototype.slice.call(grid.querySelectorAll('.product-card'));
    if (cards.length < 2) { return; }

    // The dock carries the query the page was SERVED with; the in-page field is
    // the fallback for a page that rendered without the dock.
    var mount = document.querySelector('[data-search-runs]');
    var input = document.querySelector('[data-search-input]');
    var raw = (mount && mount.getAttribute('data-query')) || (input && input.value) || '';
    var q = seriesKey(raw.replace(/\*/g, ' '));
    if (!q) { return; }
    var words = q.split(' ');

    var seen = {}, names = [], mine = [], rest = [], label = '';
    cards.forEach(function (card) {
      var key = seriesKey(card.getAttribute('data-series'));
      var hit = key && words.every(function (w) { return key.indexOf(w) >= 0; });
      if (!hit) { rest.push(card); return; }
      if (!seen[key]) { seen[key] = true; names.push(key); }
      if (!label) { label = card.getAttribute('data-series') || ''; }
      mine.push(card);
    });

    // Not one series, or nothing to hide: leave the page exactly as served.
    if (names.length !== 1 || !mine.length || !rest.length) { return; }

    rest.forEach(function (c) { c.classList.add('is-offseries'); });

    var note = document.createElement('p');
    note.className = 'search-narrowed';
    note.setAttribute('role', 'status');

    var said = document.createElement('span');
    said.textContent = 'Showing ' + mine.length + ' ' + label +
      (mine.length === 1 ? ' book' : ' books') + ' only.';
    note.appendChild(said);

    var more = document.createElement('button');
    more.type = 'button';
    more.className = 'search-narrowed__more';
    more.textContent = 'Show the other ' + rest.length +
      (rest.length === 1 ? ' result' : ' results');
    more.addEventListener('click', function () {
      rest.forEach(function (c) { c.classList.remove('is-offseries'); });
      note.removeChild(more);
      said.textContent = 'Showing all ' + cards.length + ' results.';
    });
    note.appendChild(more);

    grid.parentNode.insertBefore(note, grid.nextSibling);
  })();


  /* ── Infinite scroll: memory-bounded loader for [data-product-grid] ──
     Owner, 2026-09-18 (via Bruce): wants continuous scroll instead of
     separate pages, "make sure it doesnt mess w somones computer by using
     a ton of ram." Full spec: meta/PLAN-bruce-brief-2026-09-18.md §4.

     ONE module for every paginated grid on the site -- collection.liquid
     (20/page) and search.liquid (50/page) both mark their grid
     [data-product-grid] and both already render
     {% paginate %} <div data-product-grid> ... <nav class="pagination">
     {% endpaginate %}. That markup IS the no-JS site and is never touched
     here -- this module only reads the Next link's URL, hides the nav once
     it has taken over, and carries on from there. If any capability check
     below fails, nothing runs and the paginated site is the whole site.

     WHY BOUNDED, NOT ENDLESS: a decoded 480x640 cover costs ~1.2MB of RAM
     regardless of its file size, and the catalogue runs past 1,600 active
     books. An endless append would ask a phone to hold on the order of a
     gigabyte of decoded bitmaps plus tens of thousands of DOM nodes -- the
     tab gets killed. Two independent mechanisms keep memory flat instead:
       1. a CAPPED WINDOW of live cards. Past the cap the oldest cards are
          pulled out of the DOM and replaced by one spacer of the same
          measured height, so the scrollbar never jumps; their markup is
          cached on the spacer so scrolling back up re-renders them without
          a second fetch.
       2. BITMAP RELEASE. Any live card more than ~2 screens off-screen has
          its <img> src/srcset parked in data-* attributes, which frees the
          decoded bitmap even though the card itself is still inside the
          cap. Restored the moment it is back in range.
     The two are independent on purpose: the cap bounds DOM node count,
     release bounds decoded-image memory for the nodes still inside it. */
  (function () {
    var grids = document.querySelectorAll('[data-product-grid]');
    if (!grids.length || !window.fetch || !window.IntersectionObserver ||
        !window.DOMParser || !window.history || !window.history.replaceState) {
      return; // progressive enhancement only -- the paginated markup already works
    }

    // THE CAP SCALES DOWN TO THE DEVICE, never up. Owner, 2026-09-19:
    // "it needs to be dynamic to ram", then "why do ppl need 240 keep it 120".
    // Both are right and they resolve the same way. 120 is the ceiling because
    // nobody can look at more than a screenful at a time -- holding 240 buys the
    // visitor nothing and spends their memory for it. A weak device gets LESS,
    // which is the half of "dynamic" that actually protects anyone.
    //
    // navigator.deviceMemory reports RAM in GB, rounded down to a power of two
    // and capped at 8 by the spec, so a 64GB workstation and an 8GB laptop both
    // report 8 -- another reason not to scale upward on it. Chrome and Edge
    // expose it; Safari and Firefox do not, and there we use 120, the value
    // measured holding 1,040 cards.
    //
    // ~30 cards per GB: a decoded cover is roughly 1.2MB at the sizes this grid
    // requests, so 30 cards is ~36MB of bitmap per GB. Floor 60 so the grid
    // still works on a 0.5GB phone.
    var CAP = (function () {
      var gb = window.navigator && window.navigator.deviceMemory;
      var saveData = window.navigator && window.navigator.connection &&
                     window.navigator.connection.saveData;
      if (saveData) { return 60; }          // the visitor asked us to be frugal
      if (!gb || typeof gb !== 'number') { return 120; }  // unknown -> the tested value
      return Math.max(60, Math.min(120, Math.round(gb * 30 / 20) * 20));
    }());
    var EVICT_BATCH = 20;     // cards moved into one spacer per trim step (one collection page)
    var RELEASE_SCREENS = 2;  // release a card's bitmap this many screens off-screen

    // The browser's own scroll restoration would jump to the saved Y before
    // the pages that made that Y valid have been re-fetched in -- manual
    // means nothing moves until restoreFromHistory() below says so.
    try { window.history.scrollRestoration = 'manual'; } catch (e) { /* older Safari */ }

    function releaseImg(img) {
      if (!img || img.getAttribute('data-released')) { return; }
      var src = img.getAttribute('src');
      var srcset = img.getAttribute('srcset');
      if (src) { img.setAttribute('data-src', src); img.removeAttribute('src'); }
      if (srcset) { img.setAttribute('data-srcset', srcset); img.removeAttribute('srcset'); }
      img.setAttribute('data-released', 'true');
    }

    function restoreImgBitmap(img) {
      if (!img || !img.getAttribute('data-released')) { return; }
      var src = img.getAttribute('data-src');
      var srcset = img.getAttribute('data-srcset');
      if (src) { img.setAttribute('src', src); img.removeAttribute('data-src'); }
      if (srcset) { img.setAttribute('srcset', srcset); img.removeAttribute('data-srcset'); }
      img.removeAttribute('data-released');
    }

    // One scratch <template> turns a cached outerHTML string back into a
    // real node. <template>.content never runs the scripts or images inside
    // it until it is adopted into the document, which is what lets an
    // evicted card's bitmap stay released until it is really back in range.
    var scratch = document.createElement('template');
    function htmlToNode(str) {
      scratch.innerHTML = str;
      var node = scratch.content.firstElementChild;
      scratch.innerHTML = '';
      return node;
    }

    function shallowCopy(obj) {
      var out = {};
      for (var k in obj) { if (Object.prototype.hasOwnProperty.call(obj, k)) { out[k] = obj[k]; } }
      return out;
    }

    // G6: the title-match count is exact once there is nothing more to load.
    function titleCountDone() {
      var more = document.querySelector('[data-title-more]');
      if (more) { more.hidden = true; }
      var tc = document.querySelector('[data-title-count]'), pl = document.querySelector('[data-title-plural]');
      if (tc && pl) { pl.hidden = (parseInt(tc.textContent, 10) || 0) === 1; }
    }

    function GridLoader(grid, index) {
      this.grid = grid;
      this.key = 'rcInfinite' + index;
      this.liveCount = grid.querySelectorAll(':scope > .product-card').length;
      this.loading = false;
      this.restoring = false;
      this.pagesLoaded = 1;

      // The paginate block is a sibling of the grid inside .browse__results:
      // both templates render {% paginate %} <div data-product-grid> ...
      // <nav class="pagination"> {% endpaginate %}, so the plain sibling nav
      // is always the right one -- no per-template lookup needed.
      this.pagination = grid.parentNode ? grid.parentNode.querySelector('.pagination') : null;
      var nextLink = this.pagination ? this.pagination.querySelector('.pagination__link--next') : null;
      this.urlTemplate = nextLink ? nextLink.getAttribute('href') : null; // page=2's URL, with every filter Shopify put on it
      this.nextUrl = this.urlTemplate;

      var vh = window.innerHeight || 800;
      var releaseMargin = Math.round(vh * RELEASE_SCREENS) + 'px';
      var self = this;

      this.imgObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { restoreImgBitmap(entry.target); }
          else { releaseImg(entry.target); }
        });
      }, { rootMargin: releaseMargin + ' 0px ' + releaseMargin + ' 0px' });

      this.spacerObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { self.restoreSpacer(entry.target); }
        });
      }, { rootMargin: Math.round(vh) + 'px 0px' });

      if (this.nextUrl) {
        this.sentinel = document.createElement('div');
        this.sentinel.className = 'infinite-sentinel';
        this.sentinel.setAttribute('aria-hidden', 'true');
        this.sentinel.setAttribute('data-infinite-sentinel', '');
        grid.parentNode.insertBefore(this.sentinel, grid.nextSibling);

        this.status = document.createElement('div');
        this.status.className = 'infinite-scroll-status';
        this.status.setAttribute('data-infinite-status', '');
        this.status.hidden = true;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn btn--yellow';
        btn.setAttribute('data-infinite-loadmore', '');
        btn.textContent = 'Load More Comics';  // only ever shown after a failed fetch
        btn.addEventListener('click', function () {
          self.status.hidden = true;
          self.fetchNext(false);
        });
        this.status.appendChild(btn);
        this.sentinel.parentNode.insertBefore(this.status, this.sentinel.nextSibling);

        // A bit before the sentinel actually reaches the viewport, so the
        // next page is in by the time a visitor scrolls to where it was.
        this.sentinelObserver = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) { self.maybeAutoFetch(); }
        }, { rootMargin: '0px 0px ' + Math.round(vh) + 'px 0px' });
        this.sentinelObserver.observe(this.sentinel);
      }

      // JS has taken over: the Prev/Next nav stays in the DOM (no-JS needs
      // it exactly as Shopify rendered it) but is hidden rather than removed.
      // Both the attribute AND an inline display:none -- .pagination{display:
      // flex} in components.css/theme.css is an author rule of the same
      // specificity as the UA [hidden] rule, and author rules win ties, so
      // the attribute alone leaves the nav visible. Inline style always wins.
      if (this.pagination) {
        this.pagination.hidden = true;
        this.pagination.style.display = 'none';
      }

      Array.prototype.forEach.call(
        grid.querySelectorAll(':scope > .product-card img'),
        function (img) { self.imgObserver.observe(img); }
      );
    }

    GridLoader.prototype.maybeAutoFetch = function () {
      if (this.loading || !this.nextUrl) { return; }
      // No gate. Owner, 2026-09-19: "why is there a load more comics button I
      // want it to be dynamic". Scrolling keeps loading for as long as there
      // are pages. The button still exists but is now ONLY a failure handout --
      // showLoadMore() is reached from the fetch error path, so a dead or very
      // slow connection hands control back instead of retrying into the void.
      this.fetchNext(true);
    };

    GridLoader.prototype.showLoadMore = function () {
      if (this.sentinelObserver && this.sentinel) { this.sentinelObserver.unobserve(this.sentinel); }
      if (this.status) { this.status.hidden = false; }
    };

    GridLoader.prototype.fetchNext = function (isAuto) {
      if (this.loading || !this.nextUrl) { return; }
      this.loading = true;
      if (this.sentinelObserver && this.sentinel) { this.sentinelObserver.unobserve(this.sentinel); }
      var self = this;
      var url = this.nextUrl;
      window.fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var newGrid = doc.querySelector('[data-product-grid]');
          var cards = newGrid
            ? Array.prototype.slice.call(newGrid.querySelectorAll(':scope > .product-card'))
            : [];
          self.appendCards(cards);
          var nextLink = doc.querySelector('.pagination__link--next');
          self.nextUrl = nextLink && !(self.titleMisses >= 2) ? nextLink.getAttribute('href') : null;
          if (!self.nextUrl) { titleCountDone(); }
          self.pagesLoaded += 1;
          self.loading = false;
          self.saveHistory();
          if (!self.nextUrl) {
            if (self.sentinel && self.sentinel.parentNode) { self.sentinel.parentNode.removeChild(self.sentinel); }
            if (self.status) { self.status.hidden = true; }
          } else {
            self.sentinelObserver.observe(self.sentinel);
          }
        })
        .catch(function () {
          // A failed fetch must not spin silently forever -- hand control
          // back to the visitor rather than retrying into a slow or dead
          // connection.
          self.loading = false;
          self.showLoadMore();
        });
    };

    GridLoader.prototype.appendCards = function (cardEls) {
      // T2 sandbox (2026-10-08): a title-only search grid hides the misses, so
      // appending them added no height, the loader ran on, and the cap evicted
      // the visible hits: scrolling 'x-men' to the end left an empty page.
      // Append only the hits; two pages in a row without one end the run
      // (Shopify ranks the title hits first; fetchNext reads titleMisses).
      if (this.grid.hasAttribute('data-title-only')) {
        cardEls = cardEls.filter(function (c) { return c.getAttribute('data-title-hit') !== '0'; });
        this.titleMisses = cardEls.length ? 0 : (this.titleMisses || 0) + 1;
        // G6: the header count is the title matches; add this page's hits to it.
        var tc = document.querySelector('[data-title-count]');
        if (tc && cardEls.length) {
          var n = (parseInt(tc.textContent, 10) || 0) + cardEls.length;
          tc.textContent = String(n);
          var pl = document.querySelector('[data-title-plural]');
          if (pl) { pl.hidden = false; }
        }
      }
      if (!cardEls.length) { return; }
      var self = this;
      var imported = cardEls.map(function (card) { return document.importNode(card, true); });
      var frag = document.createDocumentFragment();
      imported.forEach(function (node) { frag.appendChild(node); self.liveCount += 1; });
      this.grid.appendChild(frag);
      imported.forEach(function (node) {
        var img = node.querySelector('img');
        if (img) { self.imgObserver.observe(img); }
      });
      this.trimExcess();
    };

    GridLoader.prototype.trimExcess = function () {
      var guard = 0;
      while (this.liveCount > CAP && guard < 100) {
        if (!this.evictOnce()) { break; }
        guard += 1;
      }
    };

    // Evicts one batch from whichever end sits farther from the viewport --
    // normally the top, since pages are appended at the bottom, but the far
    // end flips to the bottom once a spacer higher up has just been restored
    // (restoreSpacer() below calls back in here for exactly that case).
    GridLoader.prototype.evictOnce = function () {
      var cards = this.grid.querySelectorAll(':scope > .product-card');
      if (cards.length < 2) { return false; }
      var vh = window.innerHeight || 800;
      // measure rendered cards only: a hidden card (title-only search miss) has an all-zero rect
      var shown = function (c) { return c.getClientRects().length > 0; };
      var seen = Array.prototype.filter.call(cards, shown);
      if (!seen.length) { return false; }
      var firstRect = seen[0].getBoundingClientRect();
      var lastRect = seen[seen.length - 1].getBoundingClientRect();
      var aboveDist = -firstRect.top;       // positive: how far the top run sits above the viewport
      var belowDist = lastRect.bottom - vh; // positive: how far the bottom run sits below it
      var fromStart = aboveDist > belowDist;
      var n = Math.min(EVICT_BATCH, cards.length - 1); // always leave at least one live card
      var slice = fromStart
        ? Array.prototype.slice.call(cards, 0, n)
        : Array.prototype.slice.call(cards, cards.length - n);
      if (!slice.length) { return false; }

      var sliceSeen = slice.filter(shown);
      var topY = sliceSeen.length ? sliceSeen[0].getBoundingClientRect().top : 0;
      var bottomY = sliceSeen.length ? sliceSeen[sliceSeen.length - 1].getBoundingClientRect().bottom : 0;
      var self = this;
      var cachedHtml = slice.map(function (card) {
        var img = card.querySelector('img');
        // Cache the PRISTINE markup -- a card evicted while its bitmap was
        // already released must come back ready for loading="lazy" again,
        // not stuck mid-release.
        if (img) { restoreImgBitmap(img); self.imgObserver.unobserve(img); }
        return card.outerHTML;
      });

      var spacer = document.createElement('div');
      spacer.className = 'infinite-spacer';
      spacer.setAttribute('aria-hidden', 'true');
      spacer.style.gridColumn = '1 / -1';
      // Measured, not assumed: the exact vertical span the evicted run held,
      // gap included, so the scrollbar does not move when the cards leave.
      spacer.style.height = Math.max(0, Math.round(bottomY - topY)) + 'px';
      spacer._rcCards = cachedHtml;

      this.grid.insertBefore(spacer, slice[0]);
      slice.forEach(function (card) { self.grid.removeChild(card); });
      this.liveCount -= slice.length;
      this.spacerObserver.observe(spacer);
      return true;
    };

    GridLoader.prototype.restoreSpacer = function (spacer) {
      this.spacerObserver.unobserve(spacer);
      var cached = spacer._rcCards || [];
      if (!cached.length || !spacer.parentNode) {
        if (spacer.parentNode) { spacer.parentNode.removeChild(spacer); }
        return;
      }
      var self = this;
      var frag = document.createDocumentFragment();
      var nodes = [];
      // Rebuilt from the cache captured at eviction time -- never re-fetched.
      cached.forEach(function (str) {
        var node = htmlToNode(str);
        if (node) { frag.appendChild(node); nodes.push(node); }
      });
      spacer.parentNode.replaceChild(frag, spacer);
      nodes.forEach(function (card) {
        var img = card.querySelector('img');
        if (img) { self.imgObserver.observe(img); }
      });
      this.liveCount += nodes.length;
      this.trimExcess(); // restoring here can push the far end back over the cap
    };

    GridLoader.prototype.saveHistory = function () {
      if (this.restoring || !this.urlTemplate) { return; }
      var state = (window.history.state && typeof window.history.state === 'object')
        ? shallowCopy(window.history.state) : {};
      state[this.key] = {
        pagesLoaded: this.pagesLoaded,
        scrollY: window.scrollY,
        urlTemplate: this.urlTemplate
      };
      window.history.replaceState(state, '', window.location.href);
    };

    // Runs on a real reload of a page that once had more loaded than the
    // server-rendered first page -- e.g. back from a product page when the
    // browser did not serve this one from bfcache. bfcache restores (the
    // common case) already have the DOM and scroll exactly as they were and
    // skip this entirely; see the pageshow listener below.
    GridLoader.prototype.restoreFromHistory = function () {
      var state = window.history.state;
      var saved = state && state[this.key];
      if (!saved || !saved.pagesLoaded || saved.pagesLoaded <= 1 || !saved.urlTemplate) { return; }
      var target = Math.min(saved.pagesLoaded, 500); // sanity ceiling, not a real limit
      var self = this;
      // `loading` is the same guard fetchNext() checks -- without it, a
      // sentinel already in view (a short reconstructed page) could fire a
      // normal auto-fetch while this sequential rebuild is still running,
      // appending the same page twice.
      this.loading = true;
      this.restoring = true;
      if (this.sentinelObserver && this.sentinel) { this.sentinelObserver.unobserve(this.sentinel); }
      var n = 2;
      function step() {
        if (n > target || !self.nextUrl) {
          self.restoring = false;
          self.loading = false;
          if (self.nextUrl && self.sentinelObserver && self.sentinel) {
            self.sentinelObserver.observe(self.sentinel);
          }
          if (typeof saved.scrollY === 'number') {
            // The pages that make this Y valid have already been appended
            // above -- appendChild is synchronous, so the scroll can happen
            // immediately rather than waiting on a frame that may not come
            // (rAF does not run at all in some backgrounded/prerendered tabs).
            window.scrollTo(0, saved.scrollY);
          }
          return;
        }
        var url = saved.urlTemplate.replace(/([?&]page=)\d+/, '$1' + n);
        window.fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.text(); })
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var newGrid = doc.querySelector('[data-product-grid]');
            var cards = newGrid
              ? Array.prototype.slice.call(newGrid.querySelectorAll(':scope > .product-card'))
              : [];
            self.appendCards(cards);
            var nextLink = doc.querySelector('.pagination__link--next');
            self.nextUrl = nextLink && !(self.titleMisses >= 2) ? nextLink.getAttribute('href') : null;
            if (!self.nextUrl) { titleCountDone(); }
            self.pagesLoaded = n;
            n += 1;
            step();
          })
          .catch(function () {
            self.restoring = false;
            self.loading = false;
            if (self.sentinelObserver && self.sentinel) { self.sentinelObserver.observe(self.sentinel); }
          });
      }
      step();
    };

    function boot() {
      var loaders = [];
      Array.prototype.forEach.call(grids, function (grid, i) { loaders.push(new GridLoader(grid, i)); });

      var saveTimer = null;
      window.addEventListener('scroll', function () {
        // A SCROLL FALLBACK, not a duplicate of the sentinel observer.
        //
        // init() hides the Prev/Next nav and starts the "Load more" button
        // hidden, so if the sentinel's IntersectionObserver never fires the
        // visitor is left on 20 books with no nav and no button -- a dead end
        // that looks like the whole catalogue. IntersectionObserver was NOT
        // watched firing in a live scroll when this shipped (it does not fire
        // in the automated browsers available here, which report
        // visibilityState 'hidden'), so it is not something to depend on
        // alone. This makes the observer an optimisation: it fires earlier and
        // more cheaply, and if it never fires at all, scrolling still loads.
        // maybeAutoFetch() is idempotent -- it no-ops while a fetch is in
        // flight, past the auto-load cap, or at the last page.
        loaders.forEach(function (l) {
          if (!l.sentinel || l.loading) { return; }
          var box = l.sentinel.getBoundingClientRect();
          if (box.top <= window.innerHeight * 2) { l.maybeAutoFetch(); }
        });

        // SCROLLING BACK UP needs the same fallback, and for a worse failure.
        // restoreSpacer() is called ONLY by spacerObserver. If that observer
        // does not fire, a visitor who scrolls back up sees blank gaps where
        // books were and never gets them back without a reload -- the books
        // are still cached, nothing is asking for them. Loading more failing
        // is a dead end; this one looks like the page broke. Same guard:
        // restoreSpacer() unobserves and rebuilds, so a double call is safe.
        loaders.forEach(function (l) {
          if (!l.grid) { return; }
          Array.prototype.forEach.call(
            l.grid.querySelectorAll('.infinite-spacer'),
            function (spacer) {
              var r = spacer.getBoundingClientRect();
              if (r.bottom >= -window.innerHeight &&
                  r.top <= window.innerHeight * 2) { l.restoreSpacer(spacer); }
            }
          );
        });

        if (saveTimer) { return; }
        saveTimer = window.setTimeout(function () {
          saveTimer = null;
          loaders.forEach(function (l) { l.saveHistory(); });
        }, 200);
      }, { passive: true });

      window.addEventListener('pageshow', function (e) {
        if (e.persisted) { return; } // DOM and scroll are already exactly as left
        loaders.forEach(function (l) { l.restoreFromHistory(); });
      });
    }

    // Idle until the first screen has actually painted. window's load event
    // fires only once the page's own (non-lazy) resources are in, which is
    // well after first paint -- nothing here, observers included, exists
    // before then, so this never competes with the initial render. Fetching
    // only ever starts later, off a real scroll or a restored history entry.
    if (document.readyState === 'complete') {
      boot();
    } else {
      window.addEventListener('load', boot);
    }
  })();

  /* ---- carousel page 2: warmed AFTER the visible page, never before -------
     Owner, 2026-09-19: "prioritize all the stuff you see when you first load
     in then the second page of the library buttons."

     Page 2 sits behind [hidden] -> display:none, and a browser never loads a
     loading="lazy" image inside display:none. So without this the six series
     tiles are not merely deprioritised, they are not fetched at all, and the
     first arrow press waits on six cold images.

     Flipping them to eager does fetch them despite display:none. Doing it on
     window load + idle is what keeps that a win rather than a regression: the
     visible six and everything else the first screen needs are already in
     before a single byte of page 2 is asked for, so LCP is untouched. They
     keep fetchpriority="low", so even once asked for they yield to anything
     the visitor can actually see. Save-Data is honoured -- someone paying per
     megabyte should not prefetch a page they may never open. */
  (function warmSecondCarouselPage() {
    var conn = window.navigator && window.navigator.connection;
    if (conn && conn.saveData) { return; }

    function warm() {
      var hiddenPages = document.querySelectorAll('[data-carousel-page][hidden]');
      Array.prototype.forEach.call(hiddenPages, function (page) {
        Array.prototype.forEach.call(page.querySelectorAll('img[loading="lazy"]'),
          function (img) { img.loading = 'eager'; });
      });
    }

    // Both timers, whichever lands first -- warm() is idempotent, so a double
    // call costs nothing. requestIdleCallback ALONE was the first attempt and it
    // did not fire at all in a backgrounded tab (measured: page two still lazy
    // 5s after load), which is the same "assume the fancy scheduler fires" trap
    // as the IntersectionObserver fallbacks above. setTimeout always fires, so it
    // is the guarantee; idle is only there to be politer when it works.
    function schedule() {
      if (window.requestIdleCallback) { window.requestIdleCallback(warm, { timeout: 2000 }); }
      window.setTimeout(warm, 1500);
    }

    if (document.readyState === 'complete') { schedule(); }
    else { window.addEventListener('load', schedule); }
  }());


})();
